// Admin-side report-only proctoring panel — aptitude only.
//
// Reads GET /assessment/:assessmentAssignedId/proctoring-report (admin-node,
// read-only) and renders the integrity band, summary metrics, a calibration
// banner when the candidate's 3s baseline was weak, and a human-readable
// timeline. Report-only by design: nothing here ever interrupted the candidate;
// this is the after-the-fact review surface.
//
// Statuses from the API:
//   ready   — report finalized, full summary + score + band
//   pending — events ingested but report not finalized yet (show raw timeline)
//   empty   — no events at all (candidate never started / collector disabled)

import React, { useEffect, useState } from 'react'
import { Tag, Spin, Empty, Typography, Tooltip } from 'antd'
// ponytail: SafetyCertificateOutlined instead of ShieldCheckOutlined — the
// latter only landed in @ant-design/icons v4.10+; admin-react pins 4.8.0, so
// the named import bound to undefined and React threw #130 when BAND.clean.icon
// first rendered (after the 404→200 fix let the success path run). Upgrade path:
// bump @ant-design/icons and revert to ShieldCheckOutlined if desired.
import { SafetyCertificateOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import adminRequest from 'utils/adminRequest'
import { isReadingEvent, readingDetectedCount } from './proctoringView'

const { Text } = Typography

// Only 2 verdict tiers — clean / review — plus no_data for an uncaptured
// session. There is no "high_concern" tier; the verdict is driven by
// LLM-confirmed reading flags only (see student-node ProctoringReport.js).
// Labels match the corporate drawer's integrity card head.
const BAND = {
  clean: { color: 'success', label: 'No concerns', icon: <SafetyCertificateOutlined /> },
  review: { color: 'warning', label: 'Review needed', icon: <ExclamationCircleOutlined /> },
  // No camera/proctoring data was captured — NOT the same as "clean". Show it
  // neutrally so a reviewer doesn't read an empty session as verified-clean.
  no_data: { color: 'default', label: 'No proctoring data', icon: <ExclamationCircleOutlined /> },
}

// ms → "12s" / "1m 05s" / "350ms"
function fmtMs(ms) {
  if (ms == null) return '—'
  const s = ms / 1000
  if (s < 1) return `${Math.round(ms)}ms`
  if (s < 60) return `${s.toFixed(s < 10 ? 1 : 0)}s`
  const m = Math.floor(s / 60)
  const rem = Math.round(s % 60)
  return `${m}m ${String(rem).padStart(2, '0')}s`
}

// pctEyeContact arrives from the server already scaled 0–100 (ProctoringReport
// _buildReport: Math.round((1 - badGaze/denom) * 100)), so do NOT re-multiply —
// the old `p * 100` rendered 100% as "10000%".
function fmtPct(p) {
  if (p == null) return '—'
  return `${Math.round(p)}%`
}

const SEV_COLOR = { high: 'red', medium: 'orange', low: 'default' }

function Metric({ label, value, hint }) {
  return (
    <Tooltip title={hint || ''}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Text style={{ fontSize: 11, color: '#8c8c8c' }}>{label}</Text>
        <Text strong style={{ fontSize: 15, color: '#262626' }}>{value}</Text>
      </div>
    </Tooltip>
  )
}

export default function ProctoringReportPanel({ assessmentAssignedId, visible, onReport, isProctoringDisabled = false }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!visible || !assessmentAssignedId || isProctoringDisabled) {
      setData(null)
      setError(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    // ponytail: use the shared adminRequest instance (baseURL = ADMIN_API_URL +
    // raw-token auth interceptor) instead of bare axios + manual ADMIN_API_URL
    // concat. ADMIN_API_URL ends with '/', so `${ADMIN_API_URL}/assessment/...`
    // produced a double slash '//assessment' that admin-node's fastify router
    // does not match -> 404. axios combineURLs normalizes baseURL/url slashes,
    // and the interceptor attaches localStorage 'token' (else 401 jwt required).
    adminRequest
      .get(`/assessment/${assessmentAssignedId}/proctoring-report`)
      .then((resp) => {
        if (cancelled) return
        // admin-node wraps as { data: { data: <result> } }; result is the payload.
        const result = resp?.data?.data || resp?.data || {}
        setData(result)
        // Lift the integrity verdict up so the parent's "Proctoring Status" badge
        // can reflect the SAME band as this report + the PDF (instead of the old
        // face-detection-only Good/Bad which can disagree).
        if (typeof onReport === 'function') onReport(result)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err?.response?.data?.message || err?.message || 'Failed to load proctoring report')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [assessmentAssignedId, visible, isProctoringDisabled])

  if (!visible || !assessmentAssignedId) return null

  const panelTitle = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
      <Text strong style={{ fontSize: 14, color: '#262626' }}>Proctoring Integrity Report</Text>
      <Text style={{ fontSize: 11, color: '#8c8c8c' }}>(report-only — never interrupts the candidate)</Text>
    </div>
  )

  if (isProctoringDisabled) {
    return (
      <div style={{ marginBottom: 24 }}>
        {panelTitle}
        <Text style={{ fontSize: 13, color: '#595959' }}>Proctoring was disabled for this assessment.</Text>
      </div>
    )
  }
  if (loading) {
    return <div style={{ marginBottom: 24 }}>{panelTitle}<Spin size="small" tip="Loading integrity report…" /></div>
  }
  if (error) {
    return (
      <div style={{ marginBottom: 24 }}>
        {panelTitle}
        <Text type="danger" style={{ fontSize: 12 }}>{error}</Text>
      </div>
    )
  }
  if (!data) return null

  const status = data.status
  const band = BAND[data.integrityBand]
  const summary = data.summary || {}
  const timeline = data.timeline || []
  const calibWeak = summary.calibrationQuality === 'weak'
  const readingDetected = readingDetectedCount(summary)

  if (status === 'empty' || data.integrityBand === 'no_data') {
    // Proctoring turned OFF for the assessment is expected — show a neutral
    // note, not the alarming "camera off / not a clean verdict" wording that
    // belongs only to a proctored attempt that captured nothing.
    const description = data.proctoringDisabled
      ? 'Proctoring was turned off for this assessment, so no integrity data was collected.'
      : 'No proctoring data was captured for this attempt (camera off or proctoring not run). This is not a clean verdict — there is simply nothing to review.'
    return (
      <div style={{ marginBottom: 24 }}>
        {panelTitle}
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={description} />
      </div>
    )
  }

  return (
    <div style={{ marginBottom: 24 }}>
      {panelTitle}

      {status === 'pending' && (
        <div style={{ marginBottom: 8 }}>
          <Tag color="processing">Report being finalized</Tag>
          <Text style={{ fontSize: 12, color: '#8c8c8c', marginLeft: 4 }}>
            {data.events} event(s) captured so far — full integrity report generates on submit.
          </Text>
        </div>
      )}

      {/* Card head, as in the corporate drawer: the integrity score leads,
          then the band. */}
      {band && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          {data.integrityScore != null && (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <Text style={{ fontSize: 12, color: '#595959' }}>Integrity score</Text>
              <Text strong style={{ fontSize: 22, color: '#262626', lineHeight: 1 }}>
                {data.integrityScore}
                <Text style={{ fontSize: 13, color: '#8c8c8c', fontWeight: 400 }}>/100</Text>
              </Text>
            </div>
          )}
          <Tag color={band.color} icon={band.icon} style={{ fontSize: 13, padding: '2px 10px' }}>
            {band.label}
          </Tag>
        </div>
      )}

      {calibWeak && (
        <div style={{ marginBottom: 12, padding: '8px 12px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6 }}>
          <Text style={{ fontSize: 12, color: '#ad6800' }}>
            <ExclamationCircleOutlined style={{ marginRight: 6 }} />
            Calibration baseline was weak ({summary.calibrationSamples ?? 'few'} samples) — gaze readings for this attempt may be less reliable.
          </Text>
        </div>
      )}

      {/* External-reference (cheating) indicators — the high-signal Tier 1/2
          metrics. Shown first because they answer "did they look things up
          elsewhere?", which gaze alone cannot. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 12, padding: '10px 12px', background: '#fff7f6', border: '1px solid #ffd6d0', borderRadius: 6 }}>
        {/* Same rows as the corporate drawer. "Reading detected" counts
            read-aloud-pace answers plus suspected reading from another device
            (proctoringView.readingDetectedCount). Split screen and Window
            covered are not shown — corporate dropped both (they overlap
            focus loss / fullscreen exit for most readers); the engine still
            scores them and their events stay in the timeline below. */}
        {readingDetected !== null && (
          <Metric label="Reading detected" value={readingDetected} hint="Answers delivered at read-aloud pace, plus suspected reading from another device" />
        )}
        <Metric label="Tab / App switches" value={summary.tabHiddenCount ?? 0} hint="Times the candidate switched to another tab or app (a same-device lookup gaze cannot see)" />
        <Metric label="Fullscreen exits" value={summary.fullscreenExitCount ?? 0} hint="Times the candidate left fullscreen lockdown" />
        <Metric label="Second display" value={summary.secondDisplayDetected ? 'Yes' : 'No'} hint="An extended desktop / second monitor was detected at start" />
        <Metric label="Focus lost to another app" value={summary.windowFocusLostCount ?? 0} hint="Times another application or overlay took focus while the assessment stayed visible" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 12, marginBottom: 12 }}>
        <Metric label="Eye contact" value={fmtPct(summary.pctEyeContact)} hint="Share of session the candidate kept eye contact" />
        <Metric label="Looked below screen" value={fmtMs(summary.gazeBelowScreenMs)} hint="Time spent looking under the screen (reading)" />
        <Metric label="Gaze off-side" value={fmtMs(summary.gazeOffScreenMs)} hint="Time spent looking away from the screen" />
        <Metric label="Eye contact lost" value={fmtMs(summary.eyeContactLostMs)} hint="Cumulative time eye contact was lost" />
        <Metric label="No face" value={fmtMs(summary.noFaceMs)} hint="Time no face was detected" />
        <Metric label="Longest absence" value={fmtMs(summary.longestAbsenceMs)} hint="Longest single stretch the candidate was away" />
        <Metric label="Phone detected" value={summary.phoneDetections ?? 0} hint="Distinct phone-detection events" />
        <Metric label="Multiple faces" value={summary.multiFaceCount ?? 0} hint="Distinct multi-face events" />
      </div>

      {timeline.length > 0 && (
        <div>
          <Text strong style={{ fontSize: 13, color: '#262626' }}>Timeline</Text>
          <div style={{ marginTop: 6, maxHeight: 260, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 6 }}>
            {timeline.map((t, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderBottom: i < timeline.length - 1 ? '1px solid #f5f5f5' : 'none' }}>
                <Text style={{ fontSize: 11, color: '#8c8c8c', minWidth: 52 }}>{fmtMs(t.atMs)}</Text>
                <Text style={{ fontSize: 13, color: '#262626', flex: 1 }}>
                  {isReadingEvent(t) ? 'Reading detected' : t.label}
                  {isReadingEvent(t) && t.label && (
                    <Text style={{ fontSize: 11, color: '#8c8c8c', marginLeft: 6 }}>{t.label}</Text>
                  )}
                </Text>
                {t.durationMs ? <Text style={{ fontSize: 11, color: '#8c8c8c' }}>for {fmtMs(t.durationMs)}</Text> : null}
                {t.severity && <Tag color={SEV_COLOR[t.severity] || 'default'} style={{ margin: 0, fontSize: 11 }}>{t.severity}</Tag>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}